import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Department, Organization } from '../../types';
import CreateDepartment from './CreateDepartment';
import { FiChevronRight, FiChevronDown, FiEdit2, FiTrash2 } from 'react-icons/fi';
import Select from 'react-select';

interface DepartmentListProps {
  organization: Organization;
  departments: Department[];
  onDepartmentSelect: (departmentId: string) => void;
  onDepartmentsChange: () => void;
  showCreateButton?: boolean;
}

interface FlatDepartment {
  id: string;
  name: string;
  level: number;
  parent_id: string | null;
  hasChildren: boolean;
}

interface EditDepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  department: FlatDepartment;
  departments: Department[];
  organizationId: string;
  onUpdate: () => void;
}

function EditDepartmentModal({ isOpen, onClose, department, departments, organizationId, onUpdate }: EditDepartmentModalProps) {
  const [name, setName] = useState(department.name);
  const [parentId, setParentId] = useState<string | null>(department.parent_id);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen, department.id]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`/api/organizations/${organizationId}/departments/${department.id}/users`);
      setUsers(response.data.users);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await axios.put(`/api/organizations/${organizationId}/departments/${department.id}`, {
        name,
        parent_department_id: parentId
      });
      onUpdate();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update department');
    } finally {
      setIsLoading(false);
    }
  };

  const buildDepartmentOptions = (deps: Department[], level = 0): { value: string; label: string }[] => {
    return deps.flatMap(dept => [
      { value: dept.id, label: '  '.repeat(level) + dept.name },
      ...(dept.sub_departments ? buildDepartmentOptions(dept.sub_departments, level + 1) : [])
    ]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] flex flex-col">
        <h2 className="text-xl font-semibold mb-4">Edit Department</h2>
        <div className="flex-grow overflow-y-auto">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Parent Department</label>
            <Select
              value={parentId ? { 
                value: parentId, 
                label: departments.find(d => d.id === parentId)?.name || '' 
              } : null}
              onChange={(option) => setParentId(option?.value || null)}
              options={[
                { value: '', label: 'No Parent Department' },
                ...buildDepartmentOptions(departments)
              ]}
              className="w-full"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Users in Department</label>
            <div className="max-h-40 overflow-y-auto border rounded-md p-2">
              {users.length === 0 ? (
                <div className="text-sm text-gray-500 text-center py-2">No users in this department</div>
              ) : (
                users.map(user => (
                  <div key={user.id} className="flex items-center justify-between py-1">
                    <span className="text-sm">{user.name}</span>
                    <span className="text-xs text-gray-500">{user.role}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-4 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DepartmentList({
  organization,
  departments,
  onDepartmentSelect,
  onDepartmentsChange,
  showCreateButton = true
}: DepartmentListProps) {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<FlatDepartment | null>(null);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [flatDepartments, setFlatDepartments] = useState<FlatDepartment[]>([]);

  const flattenDepartments = (depts: Department[], parentId: string | null = null, level = 0): FlatDepartment[] => {
    let result: FlatDepartment[] = [];
    
    for (const dept of depts) {
      const hasChildren = dept.sub_departments && dept.sub_departments.length > 0;
      result.push({
        id: dept.id,
        name: dept.name,
        level,
        parent_id: parentId,
        hasChildren
      });
      
      if (expandedDepartments.has(dept.id) && hasChildren) {
        result = result.concat(flattenDepartments(dept.sub_departments || [], dept.id, level + 1));
      }
    }
    
    return result;
  };

  useEffect(() => {
    if (departments && departments.length > 0) {
      setFlatDepartments(flattenDepartments(departments));
    } else {
      setFlatDepartments([]);
    }
  }, [departments, expandedDepartments]);

  const toggleExpand = (departmentId: string) => {
    setExpandedDepartments(prev => {
      const next = new Set(prev);
      if (next.has(departmentId)) {
        next.delete(departmentId);
      } else {
        next.add(departmentId);
      }
      return next;
    });
  };

  const handleEdit = (department: FlatDepartment) => {
    setEditingDepartment(department);
  };

  const handleDelete = async (departmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this department?')) {
      return;
    }

    try {
      await axios.delete(`/api/organizations/${organization.id}/departments/${departmentId}`);
      onDepartmentsChange();
      
      // Emit event to notify other components
      window.dispatchEvent(new CustomEvent('departmentChanged'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete department');
    }
  };

  const handleDepartmentClick = (departmentId: string) => {
    onDepartmentSelect(departmentId);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold">Departments</h2>
      </div>

      <div className="p-4">
        {showCreateButton && organization && (
          <div className="mb-4">
            <CreateDepartment
              organizationId={organization.id}
              departments={departments}
              onDepartmentCreated={onDepartmentsChange}
            />
          </div>
        )}

        {error && (
          <div className="text-red-500 mb-4">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-4">Loading departments...</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {flatDepartments.map((department) => (
              <div
                key={department.id}
                className="p-2 flex items-center space-x-2"
                style={{
                  paddingLeft: `${department.level * 1.5 + 0.5}rem`
                }}
              >
                {department.hasChildren && (
                  <button
                    onClick={() => toggleExpand(department.id)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {expandedDepartments.has(department.id) ? (
                      <FiChevronDown className="w-4 h-4" />
                    ) : (
                      <FiChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
                {!department.hasChildren && <div className="w-4" />}
                <span
                  className="flex-grow cursor-pointer hover:text-blue-600"
                  onClick={() => handleDepartmentClick(department.id)}
                >
                  {department.name}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(department)}
                    className="text-gray-500 hover:text-blue-600"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(department.id)}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingDepartment && (
        <EditDepartmentModal
          isOpen={true}
          onClose={() => setEditingDepartment(null)}
          department={editingDepartment}
          departments={departments}
          organizationId={organization.id}
          onUpdate={onDepartmentsChange}
        />
      )}
    </div>
  );
}